import { isRecord } from '@/utils/objects';

/**
 * The leaf keys whose block carries its own negation as an `inverse` flag, so
 * a `{ not: { <key>: block } }` wrapper can be read as the same block negated.
 *
 * These are the proto leaves the campaign condition wires write a `not` around
 * (recipients filter, trigger trees, filters). A leaf whose negation lives
 * somewhere else is not listed: reading its wrapper away would show the
 * condition as its positive.
 */
const NEGATABLE_BLOCK_KEYS = [
  'segment',
  'attribute',
  'object_attribute',
  'relationship_attribute',
  'relationship',
] as const;

type NegatableBlockKey = (typeof NEGATABLE_BLOCK_KEYS)[number];

/** A stored condition with its negation read in: a negated leaf, an and of them, or the condition a double negation cancels to. */
export type NegatedCondition = { [key in NegatableBlockKey]?: NegatableBlock } | { and: NegatedCondition[] } | object;

/** A leaf's block: whatever it carries, plus the two fields negation reads and writes. */
type NegatableBlock = object & { operator?: unknown; inverse?: unknown };

/** A `!`-prefixed operator is already negated, so negating it again drops the prefix. */
function negateBlock(block: NegatableBlock): NegatableBlock {
  const { operator } = block;
  if (typeof operator === 'string' && operator.startsWith('!')) {
    return { ...block, operator: operator.slice(1) };
  }
  return { ...block, inverse: block.inverse !== true };
}

/** The single negatable leaf `{ <key>: block }` with its block negated, or `null` for any other shape. */
function negateLeaf(entries: [string, unknown][]): NegatedCondition | null {
  if (entries.length !== 1) {
    return null;
  }
  const [name, block] = entries[0];
  const key = NEGATABLE_BLOCK_KEYS.find((candidate) => candidate === name);
  if (key == null || !isRecord(block)) {
    return null;
  }
  return { [key]: negateBlock(block) };
}

function negateAll(items: readonly unknown[]): NegatedCondition[] | null {
  const negated: NegatedCondition[] = [];
  for (const item of items) {
    const child = negateCondition(item);
    if (child == null) {
      return null;
    }
    negated.push(child);
  }
  return negated.length > 0 ? negated : null;
}

/**
 * The condition a `{ not: inner }` wrapper stands for, rewritten without the
 * wrapper, or `null` when it has no reading this viewer can word.
 *
 * - A negatable leaf becomes the same leaf negated: `not in VIPs`.
 * - `not (A or B)` becomes `not A and not B`, the people a negated or-ed
 *   segment list matches.
 * - `not (A)`, a group of one, is `not A`.
 * - `not not A` is `A`.
 *
 * `not (A and B)` with two or more conditions has no reading as a list of
 * rows: distributing the `not` into each one would turn "not all of these"
 * into "none of these". Ember's recipients filter reads it as unsupported for
 * the same reason, and so does this.
 */
export function negateCondition(inner: unknown): NegatedCondition | null {
  if (!isRecord(inner)) {
    return null;
  }
  if ('not' in inner) {
    return isRecord(inner.not) ? inner.not : null;
  }
  if (Array.isArray(inner.or)) {
    const negated = negateAll(inner.or);
    return negated == null ? null : { and: negated };
  }
  if (Array.isArray(inner.and)) {
    return inner.and.length === 1 ? negateCondition(inner.and[0]) : null;
  }
  return negateLeaf(Object.entries(inner));
}
